import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { getUserData, themesAPI, ThemeLibraryItem } from "@/lib/api";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const DOMAIN_ORDER = ["Executing", "Influencing", "Relationship Building", "Strategic Thinking"];

const getThemeDisplayName = (
  theme: { name: string; nameAr?: string | null },
  t: ReturnType<typeof useTranslation>["t"],
  language: string
) => {
  const englishName = theme.name;
  const arabicName = theme.nameAr || null;

  if (language?.split("-")[0] === "ar") {
    return {
      primary: arabicName || englishName,
      secondary: arabicName ? englishName : null,
    };
  }

  return {
    primary: englishName,
    secondary: arabicName ? arabicName : null,
  };
};

/** Embeddable theme library grid, shared by the standalone /strengths-explorer page and the dashboard tab. */
export default function ThemeLibraryGrid() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(() => getUserData());
  const [themes, setThemes] = useState<ThemeLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState<string>("all");

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    const loadThemes = async () => {
      setIsLoading(true);
      try {
        const response = await themesAPI.getThemes();
        setThemes(response.data || []);
      } catch (error: any) {
        toast({
          title: t("strengthsExplorer.themesLoadFailed"),
          description: getLocalizedErrorMessage(error, "strengthsExplorer.themesLoadFailedDesc"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadThemes();
  }, [navigate, t, toast, user]);

  const themesByDomain = useMemo(() => {
    const grouped: Record<string, ThemeLibraryItem[]> = {};
    themes.forEach((theme) => {
      if (!grouped[theme.domain]) grouped[theme.domain] = [];
      grouped[theme.domain].push(theme);
    });
    return grouped;
  }, [themes]);

  return (
    <div className="min-w-0 space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-normal sm:text-3xl">
          {t("strengthsExplorer.libraryTitle")}
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {t("strengthsExplorer.librarySubtitle")}
        </p>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("strengthsExplorer.loadingThemes")}
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            <button
              type="button"
              onClick={() => setDomainFilter("all")}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                domainFilter === "all"
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t("strengthsExplorer.allDomains")}
            </button>
            {DOMAIN_ORDER.map((domain) => {
              const chipColor = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
              const isActive = domainFilter === domain;
              return (
                <button
                  key={domain}
                  type="button"
                  onClick={() => setDomainFilter(domain)}
                  className="shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"
                  style={
                    isActive
                      ? { backgroundColor: chipColor, borderColor: chipColor, color: "#fff" }
                      : { backgroundColor: `${chipColor}10`, borderColor: `${chipColor}40`, color: chipColor }
                  }
                >
                  {translateDomainName(domain, t, i18n.language)}
                </button>
              );
            })}
          </div>

          <div className="space-y-8">
          {DOMAIN_ORDER.filter((domain) => themesByDomain[domain]?.length)
            .filter((domain) => domainFilter === "all" || domainFilter === domain)
            .map((domain) => {
            const color = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
            return (
              <div key={domain} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                  <h3 className="text-xl font-bold">
                    {translateDomainName(domain, t, i18n.language)}
                  </h3>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {themesByDomain[domain].map((theme) => (
                    <Link key={theme.id} to={`/strengths-explorer/${theme.id}`} className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                      <Card
                        className="h-full border-l-4 p-5 transition-colors hover:border-primary/40"
                        style={{ borderInlineStartColor: color, borderInlineStartWidth: 4 }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-1">
                            <h4 className="text-lg font-semibold leading-tight">
                              {getThemeDisplayName(theme, t, i18n.language).primary}
                            </h4>
                            {getThemeDisplayName(theme, t, i18n.language).secondary && (
                              <span className="text-[10px] font-medium opacity-75 tracking-wide" dir="ltr">
                                {getThemeDisplayName(theme, t, i18n.language).secondary}
                              </span>
                            )}
                          </div>
                          <Badge
                            className="shrink-0 border"
                            style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                          >
                            {translateDomainName(theme.domain, t, i18n.language)}
                          </Badge>
                        </div>
                        <p className="mt-3 line-clamp-4 text-sm text-muted-foreground">
                          {isRtl ? (theme.descriptionAr || theme.description) : theme.description}
                        </p>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}
